import * as CryptoJS from "crypto-js";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Router } from "@angular/router";
import {
  ConfirmOtpPayload,
  RegisterUserData,
  ResendEmailOtpPayload,
  VerifyOtpPayload,
} from "../authentication/models/two-factor-auth.model";
import { Intercom } from '@supy-io/ngx-intercom';
import { ClientService } from "./client.service";
import { EnvService } from "./environment.service";

@Injectable({
  providedIn: "root",
})
export class UserService {
  public currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  userUrl = this.environment.baseUrl + "auth";
  incidenturl = this.environment.incidentBaseUrl + "auth";
  adminUrl = this.environment.adminUrl + "user/api/auth";
  userValid: boolean = false;
  registerUserData = new BehaviorSubject<RegisterUserData>(
    {} as RegisterUserData
  );
  httpOptions: object = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    responseType: 'text'
  };
  public hasDeviceDetails = new BehaviorSubject(false);
  constructor(
    private http: HttpClient, public environment: EnvService,
    private router: Router,
    private intercom: Intercom,
    private CS: ClientService
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("user"))
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }
  async resetUser(username) {
    const body = {
      user_name: username,
    };
    return await this.http
      .post<any>(`${this.userUrl}/resetlink`, body)
      .subscribe(async (response) => response);
  }

  setHasDevicedetails(val) {
    this.hasDeviceDetails.next(val);
  }

  login(userCreds, isFromOTP?: boolean, deviceName?) {
    userCreds.password = this.encryptString(userCreds.password, "shbvfbvf");
    const body = {
      user_name: userCreds.username,
      usr_pwd: userCreds.password,
      deviceName: deviceName
    };
    const headers = new HttpHeaders({
      platform: 'webapp'
    })
    //console.log(userCreds);
    return this.http.post<any>(`${this.userUrl}/signin`, body, { headers: headers }).pipe(
      map((user) => {
        //console.log(user);
        if (!user?.email_verified && !isFromOTP) {
          user.authToken = null;
        }
        if (user && user?.authToken) {
          this.storeUserDetails(user);
        } else {
          this.setUserDetails(user);
        }
        return user;
      })
    );
  }

  getDeviceDetails(uid, monsoon_session?) {
    const session = monsoon_session ? monsoon_session : false;
    return this.http.get(`${this.environment.adminUrl}user/api/user/getDeviceList/${uid}?monsoon_session=${session}`);
  }

  signOutDevice(uid, payload, devId, deviceName) {
    return this.http.post(`${this.environment.adminUrl}user/api/user/signoutWithDevice/${uid}?loginAttemptOpt=${devId}&deviceNameOpt=${deviceName}&platformopt=webapp`, payload);
  }

  signOutOfApp() {
    const devId = localStorage.getItem('currentDeviceId');
    const userDetails = JSON.parse(localStorage.getItem('user'))
    this.signOutDevice(userDetails?.id, [devId], devId, userDetails?.deviceName).subscribe(_res => {
      this.currentUserSubject.next(null);
      this.currentUser =
        this.currentUserSubject.asObservable();
      this.CS.activeClients.next(null);
      this.CS.inActiveClients.next(null);

      // track event into intercom
      this.intercom.trackEvent("logout", {
        email: JSON.parse(localStorage.getItem("user")).email,
        user_id: JSON.parse(localStorage.getItem("user")).id,
      });
      localStorage.clear();
      sessionStorage.clear();
      this.intercom.shutdown();
      this.router.navigate(["/authentication/signin"]);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    })
  }

  setUserDetails(user) {
    const storeUser = {
      id: user.uid,
      auditor_uid: user.id,
      email: user.user_name,
      token: user.authToken,
      guid: user.guid,
      schedule_count: user.schedule_count,
      fullname: user.fullname,
      first_name: user.fullname,
      last_name: "",
      is_cstore: user.is_cstore,
      license_type: user.license_type,
      is_enable_schedule: user.is_enable_schedule,
      role_id: user.role_id,
      // Near-miss specific fields from signin response
      client_id: user.client_id,
      audit_type_id: user.audit_type_id,
      store_id: user.store_id,
      audit_date: user.audit_date,
      seq_no: user.seq_no,
    };
    // this.currentUserSubject.next(user);
    localStorage.setItem("userDetails", JSON.stringify(storeUser));
  }

  simlogin(obj, deviceName?) {
    const body = {
      reg_token: obj.reg_token,
      deviceName: deviceName,
      monsoon_session: obj.monsoon_session
    };
    let url;
    if (obj.isapp) {
      url = `${this.incidenturl}/simlogin`;
    } else {
      url = `${this.userUrl}/simlogin`;
    }
    //console.log(userCreds);
    return this.http.post<any>(url, body).pipe(
      map((user) => {
        //console.log(user);
        if (user && user.authToken) {
          this.storeUserDetails(user);
        }
        return user;
      })
    );
  }

  /**
   * PIN Login for QR code access
   * @param barcodeValue - Token from QR code URL
   * @param pin - 4-digit PIN entered by user
   * @returns Observable with authentication response
   */
  pinLogin(barcodeValue: string, pin: string) {
    const body = {
      barcode: barcodeValue,
      pin: pin
    };
    return this.http.post<any>(`${this.userUrl}/pinlogin`, body, { headers: { hideLoader: 'true' } }).pipe(
      map((user) => {
        if (user && user.authToken) {
          this.storeUserDetails(user);
        }
        return user;
      })
    );
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }
  storeUserDetails(user) {
    const storeUser = {
      id: user.uid,
      auditor_uid: user.id || user.uid, // Use uid if id is not present (PIN login case)
      email: user.user_name,
      token: user.authToken,
      guid: user.guid,
      schedule_count: user.schedule_count,
      fullname: user.fullname,
      first_name: user.fullname,
      last_name: "",
      is_cstore: user.is_cstore,
      license_type: user.license_type,
      is_enable_schedule: user.is_enable_schedule,
      role_id: user.role_id,
      // Near-miss specific fields from signin response
      client_id: user.client_id,
      audit_type_id: user.audit_type_id,
      store_id: user.store_id,
      audit_date: user.audit_date,
      seq_no: user.seq_no,
    };
    localStorage.setItem("user", JSON.stringify(storeUser));
    this.currentUserSubject.next(user);
  }

  updateUserDetails(user, info) {
    const storeUser = {
      id: user.id,
      auditor_uid: user.id,
      email: user.email,
      token: user.token,
      guid: user.guid,
      schedule_count: user.schedule_count,
      fullname: user.fullname,
      first_name: user.first_name,
      last_name: "",
      is_cstore: user.is_cstore,
      license_type: info.license_type,
      is_enable_schedule: user.is_enable_schedule,
      role_id: user.role_id,
      // Near-miss specific fields
      client_id: user.client_id,
      audit_type_id: user.audit_type_id,
      store_id: user.store_id,
      audit_date: user.audit_date,
      seq_no: user.seq_no,
    };
    localStorage.setItem("user", JSON.stringify(storeUser));
    this.currentUserSubject.next(user);
  }

  updateUserDetailsToActualAuditor(user, meta) {
    const storeUser = {
      id: user.id,
      auditor_uid: meta.auditor_uid,
      email: user.email,
      token: user.token,
      guid: user.guid,
      schedule_count: user.schedule_count,
      fullname: user.fullname,
      first_name: user.first_name,
      last_name: "",
      is_cstore: user.is_cstore,
      is_enable_schedule: user.is_enable_schedule,
      role_id: user.role_id,
      // Near-miss specific fields
      client_id: user.client_id,
      audit_type_id: user.audit_type_id,
      store_id: user.store_id,
      audit_date: user.audit_date,
      seq_no: user.seq_no,
    };
    localStorage.setItem("user", JSON.stringify(storeUser));
    this.currentUserSubject.next(user);
  }

  updateUserDetailsToCurrentUser(user) {
    const storeUser = {
      id: user.id,
      auditor_uid: user.id,
      email: user.email,
      token: user.token,
      guid: user.guid,
      schedule_count: user.schedule_count,
      fullname: user.fullname,
      first_name: user.first_name,
      last_name: "",
      is_cstore: user.is_cstore,
      license_type: user.license_type,
      is_enable_schedule: user.is_enable_schedule,
      role_id: user.role_id,
      // Near-miss specific fields
      client_id: user.client_id,
      audit_type_id: user.audit_type_id,
      store_id: user.store_id,
      audit_date: user.audit_date,
      seq_no: user.seq_no,
    };
    localStorage.setItem("user", JSON.stringify(storeUser));
    this.currentUserSubject.next(user);
  }

  isLogggedIn() {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user !== null) {
      this.router.navigate(["/home"]);
      return true;
    } else {
      this.router.navigate(["/authentication/signin"]);
      localStorage.clear();
    }
  }

  registerUser(params: any) {
    return this.http.post<any>(`${this.userUrl}/signup`, params).pipe(
      map((user) => {
        //console.log(user);
        if (user && user.authToken) {
          this.storeUserDetails(user);
        }
        return user;
      })
    );
  }

  async changePasswordexp(obj) {
    const retrievedObject: any = localStorage.getItem("user");
    const data = JSON.parse(retrievedObject);
    obj.guid = data.guid;
    obj.uid = data.id;
    return await this.http
      .post<any>(this.environment.baseUrl + "audits/changepasswordexp", obj)
      .pipe(
        map((data) => {
          return data;
        })
      );
  }

  encryptString(message, enc_key_secret) {
    const salt = CryptoJS.lib.WordArray.random(16);
    const salt_hex = CryptoJS.enc.Hex.stringify(salt);

    const iv = CryptoJS.lib.WordArray.random(16);
    const iv_hex = CryptoJS.enc.Hex.stringify(iv);

    const key = CryptoJS.PBKDF2(enc_key_secret, salt, {
      keySize: 256 / 32,
      iterations: 10,
    });
    //var key_hex=CryptoJS.enc.Hex.stringify(key);
    const key_hex = key;

    const encrypted = CryptoJS.AES.encrypt(message, key, { iv: iv });

    // ----- base64 encoding ----------
    const encryptedtxt =
      salt_hex +
      ":" +
      iv_hex +
      ":" +
      encrypted.ciphertext.toString(CryptoJS.enc.Base64) +
      ":" +
      key_hex;
    // console.log('html - ciphere txt : ' + encryptedtxt);
    return encryptedtxt;
  }

  async usersignout() {
    const user = JSON.parse(localStorage.getItem('user'))

    const headers = new HttpHeaders({
      'skip-interceptor': '',
      Authorization: 'Bearer ' + user.token,
    });
    return await this.http
      .get<any>(this.environment.baseUrl + "auth/signout", { headers: headers }
      )
      .pipe(
        map((list) => {
          return list;
        })
      );
  }

  /**
   * Function used to confirm the otp generated via email
   * @param obj : Payload
   * @returns user details
   */
  confirmOtp(obj: ConfirmOtpPayload) {
    // const headers = new HttpHeaders({
    //   "skip-interceptor": "",
    //   Authorization: "Bearer ",
    // });
    return this.http.post<any>(`${this.adminUrl}/confirmotp`, obj).pipe(
      map((user) => {
        return user;
      })
    );
  }

  /**
   * Function used to verify the otp entered
   * @param obj : Payload
   * @returns : User Details
   */
  verifyOtp(obj: VerifyOtpPayload) {
    const headers = new HttpHeaders({
      "skip-interceptor": "",
      Authorization: "Bearer ",
    });
    obj.webapp = true;
    return this.http.post<any>(`${this.adminUrl}/verify`, obj, {
      headers: headers,
      responseType: 'json',
    });
  }

  /**
   * Function used to resend email otp if user has lost his QR code for otp
   * @param obj : Payload
   * @returns : Whether email has been generated or not
   */
  resendEmailOtp(obj: ResendEmailOtpPayload) {
    // const headers = new HttpHeaders({
    //   "skip-interceptor": "",
    //   Authorization: "Bearer ",
    // });
    return this.http.post<any>(`${this.adminUrl}/resendemailotp`, obj).pipe(
      map((user) => {
        return user;
      })
    );
  }
  /**
   * Function used to set the registered user data
   * @param data : Registered User Data
   */
  setRegisterUserData(data: RegisterUserData) {
    this.registerUserData.next(data);
  }

  getUserLocationCreateAccess(params: any) {
    return this.http.post<any>(`${this.environment.baseUrl}audits/user/locationaccess`, params);
  }
  getLockAuditDate(payload) {
    return this.http.post<any>(`${this.environment.baseUrl}audits/user/lockdate`, payload);

  }


  getincidentcrosss(params: any) {
    return this.http.post<any>(this.incidenturl + "/crossincident", params);
  }
  getMlFlage() {
    return this.http.get<any>(this.environment.adminUrl + "user/api/user/getmluploadflag");
  }
  resendVerifyOtp(obj: ResendEmailOtpPayload) {
    const headers = new HttpHeaders({
      'skip-interceptor': '',
      Authorization: 'Bearer ',
    });
    return this.http
      .post<any>(`${this.environment.otpUrl}/resendverifyotp`, obj, {
        headers: headers,
        responseType: 'json',
      })
      .pipe(
        map((user) => {
          return user;
        })
      );
  }

  verifyUserEmail(obj: ConfirmOtpPayload) {
    const headers = new HttpHeaders({
      'skip-interceptor': '',
      Authorization: 'Bearer ',
    });
    return this.http
      .post<any>(`${this.environment.otpUrl}/verifyuser`, obj, {
        headers: headers,
        responseType: 'json',
      })
      .pipe(
        map((user) => {
          return user;
        })
      );
  }

  getVillageDetails(guid, uid) {
    return this.http.get(`${this.environment.adminUrl}user/api/user/getUserDetBasedOnGPFlag/${guid}/${uid}`)
  }

  getVillageChangeAuthToken(selectedVillage) {
    return this.http.get(`${this.environment.adminUrl}user/api/user/getJWTToken/${selectedVillage?.guid}/${selectedVillage?.uid}/${selectedVillage?.user_name}`, this.httpOptions)
  }
}
